import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useContext, useRef } from 'react';
import type { OfficeDetailsProps } from './OfficeDetails.data';
import styles from './OfficeDetails.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './OfficeDetails.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import { MARK_LINK, NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import PrimaryLink from '../../atoms/primary-link/PrimaryLink';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import OfficeContactInfo from './OfficeContactInfo';
import Link from 'next/link';
import type { AhrefLink, ImageLink } from '../../../util/storyblok/types/global.types';
import type { SiteContextType } from '../../unlisted/SiteContext';
import { SiteContext } from '../../unlisted/SiteContext';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import { OfficeEmail } from '../location/OfficeEmail';

export default function OfficeDetails({
  isVisible,
  className,
  blok,
  ref,
}: OfficeDetailsProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const { imageModalRef } = useContext(SiteContext) as SiteContextType;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  const officesCount = blok.offices.length;

  return (
    <div
      ref={elementRef}
      className={classNames(styles.officeDetails, className)}
      {...sbEditable(blok)}
    >
      <div
        className={classNames(styles.officesContainer, styles.threeColumn, {
          [styles.fourColumn]:
            officesCount % 4 === 0 || (officesCount % 3 !== 0 && officesCount % 3 !== 2),
          [styles.hiddenDesktop]: officesCount < 3,
        })}
      >
        {blok.offices.map((office) => (
          <OfficeContactInfo key={office._uid} office={office} />
        ))}
      </div>

      <div
        className={classNames(styles.flexContainer, {
          [styles.topSeparator]: officesCount > 2,
        })}
      >
        <div className={classNames(styles.leftContainer)}>
          <div
            className={classNames(styles.leftOfficesContainer, {
              [styles.hiddenMobile]: officesCount < 3,
              [styles.hidden]: officesCount > 2,
            })}
          >
            {blok.offices.map((office) => (
              <OfficeContactInfo key={office._uid} office={office} />
            ))}
          </div>

          <div className={styles.socialLinksContainer}>
            {blok.global_emails?.length > 0 && (
              <ul className={styles.emailsContainer}>
                {blok.global_emails.map((email) => (
                  <OfficeEmail email={email} key={email._uid} />
                ))}
              </ul>
            )}

            {blok.socials_title && (
              <Heading
                as="h6"
                size="label-small"
                disableTriggerInOnScroll={false}
                transitionInType="slideUpFadeIn"
                transitionOutType="fadeOut"
                className={styles.socialsTitle}
              >
                {blok.socials_title}
              </Heading>
            )}

            {blok.socials_links.map((link) =>
              link.component === 'image_link' ? (
                <PrimaryLink
                  key={link._uid}
                  textVariant="body-standard"
                  className={classNames(styles.social)}
                  disableTriggerInOnScroll={false}
                  transitionInType="splitLineRotateIn"
                  label={link.title}
                  onClick={() =>
                    imageModalRef?.current?.open({
                      image: (link as ImageLink)?.image,
                      title: link.title,
                    })
                  }
                />
              ) : (
                <PrimaryLink
                  key={link._uid}
                  textVariant="body-standard"
                  className={classNames(styles.social)}
                  link={{
                    href: (link as AhrefLink)?.link.url || (link as AhrefLink)?.link.cached_url,
                  }}
                  disableTriggerInOnScroll={false}
                  transitionInType="splitLineRotateIn"
                  label={link.title}
                  target={(link as AhrefLink)?.link.target}
                />
              ),
            )}
          </div>
        </div>
        <div className={styles.textContainer}>
          {render(blok.description, {
            nodeResolvers: {
              [NODE_PARAGRAPH]: (children) => (
                <Text
                  className={styles.description}
                  as="p"
                  size="body-large"
                  transitionInType="splitCharsWordsRichText"
                  disableTriggerInOnScroll={false}
                >
                  {children}
                </Text>
              ),
            },
            markResolvers: {
              [MARK_LINK]: (children, props) => {
                if (props.linktype === 'story') {
                  return (
                    <Link href={`${props.href}#${props.anchor}`} passHref>
                      {}
                      {children}
                    </Link>
                  );
                }
                return <a {...props}>{children}</a>;
              },
            },
          })}
        </div>
      </div>
    </div>
  );
}
